import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getMembers, deleteMember, getMemberDetail, addDependent, deleteDependent, addMemberFollowup, getSpiritualFathers, updateMember, changeMemberStatus, importMembers, verifyPassword, bulkAssignToFather, getNationalities } from '../../services/api';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiX, FiUser, FiEye, FiUsers, FiChevronDown, FiChevronRight, FiDownload, FiUpload, FiFileText } from 'react-icons/fi';
import { downloadCSV, parseCSV, readFileAsText } from '../../utils/csv';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Toast from '../../components/Toast';
import DependentCard from '../../components/DependentCard';
import PhoneLink from '../../components/PhoneLink';

const currentYear = new Date().getFullYear();
const calcAge = (y) => y && !isNaN(y) ? currentYear - parseInt(y) : null;
const statusPill = { member: 'pill-green', inactive: 'pill-red', transferred: 'pill-blue', changed_religion: 'pill-gray', deceased: 'pill-gray', suspended: 'pill-red' };
const relColors = { wife: 'pill-purple', husband: 'pill-blue', son: 'pill-blue', daughter: 'pill-purple', other: 'pill-gray' };

const AllMembers = () => {
  const { t } = useTranslation();
  const statusLabel = {
    member: t('status.member'),
    inactive: t('status.inactive'),
    transferred: t('status.transferred'),
    changed_religion: t('status.changed_religion'),
    deceased: t('status.deceased'),
    suspended: t('status.suspended'),
  };
  const STATUS_OPTIONS = [
    { value: 'member',           label: t('status.member') },
    { value: 'inactive',         label: t('status.inactive') },
    { value: 'transferred',      label: t('status.transferred') },
    { value: 'changed_religion', label: t('status.changed_religion') },
    { value: 'deceased',         label: t('status.deceased') },
    { value: 'suspended',        label: t('status.suspended') },
    { value: 'other',            label: t('allMembers.otherSpecify') },
  ];
  const REASONS = {
    transferred:      [['Moved to another city', t('allMembers.reasonMovedCity')], ['Moved to another country', t('allMembers.reasonMovedCountry')], ['Joined another church', t('allMembers.reasonJoinedAnother')], ['Other', t('allMembers.reasonOther')]],
    changed_religion: [['Personal decision', t('allMembers.reasonPersonal')], ['Marriage', t('allMembers.reasonMarriage')], ['Other', t('allMembers.reasonOther')]],
    deceased:         [['Natural causes', t('allMembers.reasonNatural')], ['Illness', t('allMembers.reasonIllness')], ['Accident', t('allMembers.reasonAccident')], ['Other', t('allMembers.reasonOther')]],
    suspended:        [['Disciplinary action', t('allMembers.reasonDisciplinary')], ['Own request', t('allMembers.reasonOwnRequest')], ['Other', t('allMembers.reasonOther')]],
    inactive:         [['Stopped attending', t('allMembers.reasonStoppedAttending')], ['Personal reasons', t('allMembers.reasonPersonalReasons')], ['Health issues', t('allMembers.reasonHealth')], ['Other', t('allMembers.reasonOther')]],
    visitor:          [['New visitor', t('allMembers.reasonNewVisitor')], ['Other', t('allMembers.reasonOther')]],
    member:           [['Rejoined', t('allMembers.reasonRejoined')], ['Re-activated', t('allMembers.reasonReactivated')], ['Other', t('allMembers.reasonOther')]],
    other:            [['Other', t('allMembers.reasonOther')]],
  };
  const navigate = useNavigate();
  const location = useLocation();
  // v1.9.90 — same component powers two routes:
  //   /members         → only active members (membership_status = 'member')
  //   /members/former  → everyone else (inactive / transferred / deceased / etc.)
  // Mode is derived from the URL so the Add/Status buttons and page title can
  // adapt, and the backend fetch picks between `status=member` and
  // `exclude_status=member`.
  const isFormerView = location.pathname.endsWith('/former');
  const { user } = useAuth();
  const canManage = (m) => user?.role === 'admin' || (user?.member_id && m.spiritual_father_id === user.member_id);
  const [members, setMembers] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [search, setSearch] = useState('');
  // Status filter: in the main view, default to 'member' so the list no longer
  // mixes active with inactive/transferred/deceased by default. In Former view
  // leave empty so the "exclude_status" param drives it.
  const [statusFilter, setStatusFilter] = useState(isFormerView ? '' : 'member');
  const [fatherFilter, setFatherFilter] = useState('');
  const [nationalityFilter, setNationalityFilter] = useState('');
  const [nationalities, setNationalities] = useState([]);
  const [subFilter, setSubFilter] = useState(''); // '', 'yes', 'no'
  // v1.9.54 — sort dropdown. Default 'card' preserves the pre-sort card-order
  // browsing muscle memory. All sorts run client-side after fetch.
  const [sortBy, setSortBy] = useState('card');
  const [toast, setToast] = useState(null);
  // Import-result modal so skipped rows + their reasons are visible (not just console).
  const [importReport, setImportReport] = useState(null);
  const [viewMember, setViewMember] = useState(null);
  const [editMember, setEditMember] = useState(null);
  const [expandedMember, setExpandedMember] = useState(null);
  const [memberDependents, setMemberDependents] = useState({});
  const [statusModal, setStatusModal] = useState(null);
  const [statusForm, setStatusForm] = useState({ new_status: '', reason: '', custom_reason: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newDep, setNewDep] = useState({ name: '', relationship: 'wife', birth_year: '', spiritual_status: 'unknown' });
  const [addingDep, setAddingDep] = useState(false);
  const [addingFollowup, setAddingFollowup] = useState(false);
  const [newFollowup, setNewFollowup] = useState({ date: new Date().toISOString().slice(0, 10), notes: '', status: 'unknown', next_followup_date: '' });

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const fetchMembers = () => {
    // Former view: when no specific status is chosen in the dropdown, exclude
    // active members so the list only shows former ones. When a specific
    // non-member status is picked, use that directly.
    const params = {
      search,
      father_id: fatherFilter,
      nationality: nationalityFilter,
      has_subscription: subFilter,
    };
    if (isFormerView) {
      if (statusFilter) params.status = statusFilter;
      else params.exclude_status = 'member';
    } else {
      params.status = statusFilter;
    }
    return getMembers(params).then(r => setMembers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };

  useEffect(() => { fetchMembers(); /* eslint-disable-next-line */ }, [search, statusFilter, fatherFilter, nationalityFilter, subFilter, isFormerView]);
  // Re-sync default status when the route flips (e.g. user navigates from
  // /members to /members/former inside the same mount via nav links).
  useEffect(() => { setStatusFilter(isFormerView ? '' : 'member'); }, [isFormerView]);
  useEffect(() => { getSpiritualFathers().then(r => setLeaders(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
  useEffect(() => { getNationalities().then(r => setNationalities(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);

  const openView = (m) => getMemberDetail(m.id).then(r => setViewMember(r.data || null)).catch(() => {});

  const openStatusModal = (m) => {
    setStatusModal(m);
    setStatusForm({ new_status: '', reason: '', custom_reason: '', notes: '' });
  };

  const handleStatusChange = async () => {
    if (!statusForm.new_status) return showToast(t('allMembers.selectNewStatusError'), 'error');
    setSaving(true);
    try {
      await changeMemberStatus(statusModal.id, statusForm);
      showToast(t('allMembers.statusUpdated'));
      setStatusModal(null);
      fetchMembers();
      if (viewMember?.id === statusModal.id) {
        getMemberDetail(statusModal.id).then(r => setViewMember(r.data || null));
      }
    } catch (e) { showToast(e.response?.data?.error || t('myProfile.failed'), 'error'); }
    finally { setSaving(false); }
  };

  const toggleExpand = (m) => {
    if (expandedMember === m.id) { setExpandedMember(null); return; }
    setExpandedMember(m.id);
    if (!memberDependents[m.id]) {
      getMemberDetail(m.id).then(r => setMemberDependents(p => ({ ...p, [m.id]: r.data?.dependents || [] }))).catch(() => {});
    }
  };

  // Bulk-assign-to-father. Checkboxes appear only on rows where the member
  // has no spiritual father yet; this is first-time assignment only (no
  // mass-reassign, per the design decision agreed with the user).
  const [selected, setSelected]       = useState(new Set());
  const [bulkFather, setBulkFather]   = useState('');
  const [bulkBusy, setBulkBusy]       = useState(false);
  const isUnassigned = (m) => !m.spiritual_father_id && !m.spiritual_father_name;
  // v1.9.54 — client-side sort. Card # is parsed to int so MCN-0009 comes
  // before MCN-0010 (string sort would put them the wrong way round).
  const sortedMembers = React.useMemo(() => {
    const arr = [...members];
    const nameOf = (m) => [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(' ').toLowerCase();
    const cardNum = (m) => {
      const n = parseInt(String(m.card_number || '').replace(/[^0-9]/g, ''), 10);
      return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
    };
    switch (sortBy) {
      case 'card':      arr.sort((a, b) => cardNum(a) - cardNum(b)); break;
      case 'card_desc': arr.sort((a, b) => cardNum(b) - cardNum(a)); break;
      case 'name':      arr.sort((a, b) => nameOf(a).localeCompare(nameOf(b))); break;
      case 'name_desc': arr.sort((a, b) => nameOf(b).localeCompare(nameOf(a))); break;
      case 'newest':    arr.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || ''))); break;
      case 'oldest':    arr.sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || ''))); break;
      default: break;
    }
    return arr;
  }, [members, sortBy]);
  const visibleUnassignedIds = sortedMembers.filter(isUnassigned).map(m => m.id);
  const allVisibleSelected = visibleUnassignedIds.length > 0 && visibleUnassignedIds.every(id => selected.has(id));
  const toggleOne = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAllVisible = () => {
    if (allVisibleSelected) setSelected(prev => {
      const next = new Set(prev);
      visibleUnassignedIds.forEach(id => next.delete(id));
      return next;
    });
    else setSelected(prev => {
      const next = new Set(prev);
      visibleUnassignedIds.forEach(id => next.add(id));
      return next;
    });
  };
  const clearSelection = () => { setSelected(new Set()); setBulkFather(''); };
  const handleBulkAssign = async () => {
    if (!bulkFather || selected.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await bulkAssignToFather({ father_id: parseInt(bulkFather), member_ids: Array.from(selected) });
      showToast(data.message || `${selected.size} assigned`);
      clearSelection();
      fetchMembers();
      getSpiritualFathers().then(r => setLeaders(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    } catch (e) {
      showToast(e.response?.data?.error || t('myProfile.failed'), 'error');
    } finally { setBulkBusy(false); }
  };

  const [deleteModal, setDeleteModal] = useState(null); // { member, password, error, busy }
  const handleDelete = (m) => setDeleteModal({ member: m, password: '', error: '', busy: false });
  const confirmDelete = async () => {
    if (!deleteModal?.password) {
      setDeleteModal(p => ({ ...p, error: t('allMembers.passwordRequired') }));
      return;
    }
    setDeleteModal(p => ({ ...p, busy: true, error: '' }));
    try {
      await verifyPassword({ password: deleteModal.password });
    } catch (e) {
      setDeleteModal(p => ({ ...p, busy: false, error: e.response?.data?.error || t('allMembers.incorrectPassword') }));
      return;
    }
    try {
      await deleteMember(deleteModal.member.id);
      const name = `${deleteModal.member.first_name} ${deleteModal.member.last_name}`;
      setDeleteModal(null);
      showToast(t('allMembers.memberDeleted', { name }), 'error');
      fetchMembers();
    } catch (e) {
      setDeleteModal(p => ({ ...p, busy: false, error: e.response?.data?.error || t('myProfile.failed') }));
    }
  };

  const handleEditSave = async () => {
    setError('');
    if (!editMember.first_name || !editMember.last_name) return setError(t('allMembers.nameRequired'));
    setSaving(true);
    try {
      await updateMember(editMember.id, editMember);
      setEditMember(null);
      showToast(t('members.memberUpdated'));
      fetchMembers();
    } catch (err) { setError(err.response?.data?.error || t('myProfile.failed')); }
    finally { setSaving(false); }
  };

  const handleAddDep = async () => {
    if (!newDep.name) return;
    setSaving(true);
    try {
      await addDependent(viewMember.id, newDep);
      setAddingDep(false);
      setNewDep({ name: '', relationship: 'wife', birth_year: '', spiritual_status: 'unknown' });
      const updated = await getMemberDetail(viewMember.id);
      setViewMember(updated.data);
      showToast(t('myProfile.dependentAdded'));
    } catch (e) { showToast(t('myProfile.failed'), 'error'); }
    finally { setSaving(false); }
  };

  const handleDeleteDep = async (depId) => {
    if (!window.confirm(t('allMembers.removeDependentConfirm'))) return;
    await deleteDependent(depId);
    const updated = await getMemberDetail(viewMember.id);
    setViewMember(updated.data);
    showToast(t('myProfile.removed'), 'error');
  };

  const handleAddFollowup = async () => {
    if (!newFollowup.date) return;
    setSaving(true);
    try {
      await addMemberFollowup(viewMember.id, newFollowup);
      setAddingFollowup(false);
      setNewFollowup({ date: new Date().toISOString().slice(0, 10), notes: '', status: 'unknown', next_followup_date: '' });
      const updated = await getMemberDetail(viewMember.id);
      setViewMember(updated.data);
      showToast(t('myMembers.followupRecorded'));
    } catch (e) { showToast(t('myProfile.failed'), 'error'); }
    finally { setSaving(false); }
  };

  const setE = f => e => setEditMember(p => ({ ...p, [f]: e.target.value }));
  const setD = f => e => setNewDep(p => ({ ...p, [f]: e.target.value }));
  const setF = f => e => setNewFollowup(p => ({ ...p, [f]: e.target.value }));
  const today = new Date().toISOString().slice(0, 10);

  const CSV_COLUMNS = [
    { key: 'title', label: 'title' },
    { key: 'first_name', label: 'first_name' },
    { key: 'middle_name', label: 'middle_name' },
    { key: 'last_name', label: 'last_name' },
    { key: 'christian_name', label: 'christian_name' },
    { key: 'gender', label: 'gender' },
    { key: 'birth_year', label: 'birth_year' },
    { key: 'marital_status', label: 'marital_status' },
    { key: 'occupation', label: 'occupation' },
    { key: 'nationality', label: 'nationality' },
    { key: 'phone', label: 'phone' },
    { key: 'whatsapp', label: 'whatsapp' },
    { key: 'email', label: 'email' },
    { key: 'address', label: 'address' },
    { key: 'holy_communion', label: 'holy_communion' },
    { key: 'join_date', label: 'join_date' },
    { key: 'membership_status', label: 'membership_status' },
    { key: 'department', label: 'department' },
    { key: 'zone', label: 'zone' },
    { key: 'notes', label: 'notes' },
    { key: 'is_spiritual_father', label: 'is_spiritual_father', get: r => r.is_spiritual_father ? 'true' : 'false' },
    { key: 'spiritual_father_name', label: 'spiritual_father_name' },
    // Clergy-only — fill in only when title is Priest or Deacon. The backend ignores them otherwise.
    { key: 'ordination_date', label: 'ordination_date' },
    { key: 'ordained_by', label: 'ordained_by' },
  ];

  const handleExport = () => {
    if (members.length === 0) { showToast('No members to export', 'error'); return; }
    downloadCSV(`members-${today}.csv`, CSV_COLUMNS, members);
  };

  const handleSampleCSV = () => {
    // Two rows: a lay member showing every common field, and a priest showing
    // that ordination_date + ordained_by only matter when title is clergy.
    const sample = [
      {
        title: 'Mr.', first_name: 'Sirak', middle_name: 'Solomon', last_name: 'Tesfay',
        christian_name: 'Gebriel', gender: 'male', birth_year: 1990, marital_status: 'married',
        occupation: 'Engineer', nationality: 'Zambian',
        phone: '+260977123456', whatsapp: '+260977123456', email: 'sirak@example.com',
        address: 'Lusaka', holy_communion: 'taking', join_date: '2024-01-15',
        membership_status: 'member', department: 'Sunday School', zone: 'Zone A',
        notes: 'Active member', is_spiritual_father: false, spiritual_father_name: '',
        ordination_date: '', ordained_by: '',
      },
      {
        title: 'Priest', first_name: 'Teklom', middle_name: 'Welday', last_name: 'Tikue',
        christian_name: '', gender: 'male', birth_year: 1965, marital_status: 'married',
        occupation: 'Clergy', nationality: 'Ethiopian',
        phone: '+260977000111', whatsapp: '', email: '',
        address: 'Lusaka', holy_communion: 'taking', join_date: '2010-09-01',
        membership_status: 'member', department: 'Spiritual Care', zone: 'Zone A',
        notes: '', is_spiritual_father: true, spiritual_father_name: '',
        ordination_date: '1995-06-12', ordained_by: 'Abune Antonios',
      },
    ];
    downloadCSV('members-sample.csv', CSV_COLUMNS, sample);
  };

  const fileInputRef = React.useRef(null);
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const rows = parseCSV(text);
      if (rows.length === 0) { showToast('CSV is empty', 'error'); return; }
      if (!window.confirm(`Import ${rows.length} member${rows.length === 1 ? '' : 's'}?`)) return;
      const r = await importMembers(rows);
      const skipped = r.data.errors?.length || 0;
      showToast(`Imported ${r.data.imported} of ${r.data.total}${skipped ? ` (${skipped} skipped)` : ''}`, skipped ? 'error' : 'success');
      // Open a modal with the per-row reasons so the admin can see exactly
      // which rows failed and fix them (the toast only summarises).
      if (skipped) setImportReport({ imported: r.data.imported, total: r.data.total, errors: r.data.errors, rows });
      fetchMembers();
    } catch (e) { showToast(e.response?.data?.error || 'Import failed', 'error'); }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{isFormerView ? 'Former Members' : t('members.title')}</h1><p>{members.length} {members.length === 1 ? 'family' : 'families'}{isFormerView ? ' · inactive / transferred / deceased / suspended / changed religion' : ''}</p></div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={handleSampleCSV}><FiFileText /> Sample CSV</button>
          <button className="btn btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()}><FiUpload /> Import</button>
          <button className="btn btn-secondary btn-sm" onClick={handleExport}><FiDownload /> Export</button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleImportFile} />
          <button className="btn btn-primary" onClick={() => navigate('/members/add')}><FiPlus /> {t('nav.addMember')}</button>
        </div>
      </div>

      <div className="filters-row">
        <div className="search-input-container" style={{ margin: 0, flex: 1, maxWidth: 300 }}>
          <FiSearch style={{ color: '#9ca3af' }} />
          <input placeholder={t('allMembers.searchByNameOrPhone', { defaultValue: 'Search by name, phone or MCN…' })} value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {/* v1.9.91 — the main /members view is locked to active members only.
            Any former-status filtering happens on the /members/former page so
            the two tabs stay cleanly separated. The dropdown only renders in
            Former view, where it picks between the five archived statuses. */}
        {isFormerView && (
          <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All former statuses</option>
            <option value="inactive">{t('status.inactive')}</option>
            <option value="transferred">{t('status.transferred')}</option>
            <option value="changed_religion">{t('status.changed_religion')}</option>
            <option value="deceased">{t('status.deceased')}</option>
            <option value="suspended">{t('status.suspended')}</option>
          </select>
        )}
        <select className="filter-select" value={fatherFilter} onChange={e => setFatherFilter(e.target.value)}>
          <option value="">{t('common.all')} {t('members.spiritualFather')}</option>
          <option value="unassigned">— {t('members.unassigned')} —</option>
          {leaders.map(l => <option key={l.id} value={l.id}>{l.first_name} {l.last_name}</option>)}
        </select>
        <select className="filter-select" value={nationalityFilter} onChange={e => setNationalityFilter(e.target.value)}>
          <option value="">{t('allMembers.allNationalities', { defaultValue: 'All Nationalities' })}</option>
          <option value="__blank__">— {t('allMembers.noNationality', { defaultValue: 'No nationality' })} —</option>
          {nationalities.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <select className="filter-select" value={subFilter} onChange={e => setSubFilter(e.target.value)}>
          <option value="">{t('allMembers.subscriptionAny', { defaultValue: 'Subscription: Any' })}</option>
          <option value="yes">{t('allMembers.subscriptionYes', { defaultValue: 'Has subscription' })}</option>
          <option value="no">{t('allMembers.subscriptionNo', { defaultValue: 'No subscription' })}</option>
        </select>
        {/* v1.9.54 — Sort by. All sorts run client-side after fetch, so
            switching is instant and doesn't hit the backend. */}
        <select className="filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="card">Sort: Card # (asc)</option>
          <option value="card_desc">Sort: Card # (desc)</option>
          <option value="name">Sort: Name (A→Z)</option>
          <option value="name_desc">Sort: Name (Z→A)</option>
          <option value="newest">Sort: Newest first</option>
          <option value="oldest">Sort: Oldest first</option>
        </select>
      </div>

      {selected.size > 0 && (
        <div style={{ position: 'sticky', top: 0, zIndex: 5, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', boxShadow: '0 2px 8px rgba(79,70,229,0.1)' }}>
          <strong style={{ color: '#4f46e5', fontSize: 13 }}>{selected.size} selected</strong>
          <select
            value={bulkFather}
            onChange={e => setBulkFather(e.target.value)}
            style={{ padding: '7px 10px', border: '1.5px solid #c7d2fe', borderRadius: 8, fontSize: 13, background: '#fff', minWidth: 220 }}
          >
            <option value="">{t('common.select') || '— select —'} {t('members.spiritualFather')}</option>
            {leaders.map(l => (
              <option key={l.id} value={l.id}>
                {l.title ? `${l.title} ` : ''}{l.first_name}{l.middle_name ? ` ${l.middle_name}` : ''}{l.last_name ? ` ${l.last_name}` : ''}
              </option>
            ))}
          </select>
          <button
            className="btn btn-sm"
            disabled={!bulkFather || bulkBusy}
            onClick={handleBulkAssign}
            style={{ background: '#4f46e5', color: '#fff', border: '1px solid #4338ca', padding: '7px 14px', fontSize: 13, fontWeight: 600, opacity: (!bulkFather || bulkBusy) ? 0.5 : 1 }}
          >
            {bulkBusy ? (t('common.saving') || 'Saving…') : `Assign ${selected.size}`}
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={clearSelection}
            disabled={bulkBusy}
            style={{ fontSize: 12 }}
          >{t('common.cancel')}</button>
        </div>
      )}

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                {visibleUnassignedIds.length > 0 && (
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    title={`Select all ${visibleUnassignedIds.length} unassigned on this page`}
                    style={{ cursor: 'pointer' }}
                  />
                )}
              </th>
              <th></th><th>#</th><th>{t('members.membershipCard')}</th><th>{t('common.name')}</th><th>{t('common.phone')}</th><th>{t('common.status')}</th><th>{t('members.spiritualFather')}</th><th>{t('members.dependents')}</th><th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedMembers.length === 0 ? (
              <tr><td colSpan={10}><div className="empty-state"><div className="empty-icon"><FiUser /></div><p>{t('finance.noRecords')}</p></div></td></tr>
            ) : sortedMembers.map((m, i) => {
              const hasDeps = m.dependent_count > 0;
              const isExpanded = expandedMember === m.id;
              return (
                <React.Fragment key={m.id}>
                  <tr style={{ background: hasDeps ? '#f5f3ff' : 'transparent', borderLeft: hasDeps ? '3px solid #7c3aed' : '3px solid transparent', cursor: 'pointer' }}
                    onClick={() => hasDeps && toggleExpand(m)}
                    onDoubleClick={() => navigate(`/members/profile/${m.id}`)}>
                    <td style={{ width: 32 }} onClick={e => e.stopPropagation()}>
                      {isUnassigned(m) ? (
                        <input
                          type="checkbox"
                          checked={selected.has(m.id)}
                          onChange={() => toggleOne(m.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      ) : null}
                    </td>
                    <td className="col-icon" style={{ width: 28, color: '#9ca3af' }}>
                      {hasDeps ? (isExpanded ? <FiChevronDown /> : <FiChevronRight />) : null}
                    </td>
                    <td className="col-num" data-label="#">{i + 1}</td>
                    <td data-label={t('members.membershipCard')}><span className="pill pill-gray" style={{ fontFamily: 'monospace', fontSize: 11 }}>{m.card_number || '—'}</span></td>
                    <td data-label={t('common.name')}>
                      <strong>{m.title ? `${m.title} ` : ''}{m.first_name} {m.middle_name || ''} {m.last_name}</strong>
                      {m.is_spiritual_father === 1 && <span className="pill pill-purple" style={{ marginLeft: 6, fontSize: 10 }}>{t('members.leader')}</span>}
                      {m.matched_dependent && (
                        <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 3 }}>
                          ↳ matched: <strong>{m.matched_dependent.name}</strong> <span style={{ textTransform: 'capitalize' }}>({m.matched_dependent.relationship})</span>
                        </div>
                      )}
                    </td>
                    <td data-label={t('common.phone')}><PhoneLink phone={m.phone} whatsapp={m.whatsapp} /></td>
                    <td data-label={t('common.status')}><span className={`pill ${statusPill[m.membership_status]}`}>{statusLabel[m.membership_status] || m.membership_status}</span></td>
                    <td data-label={t('members.spiritualFather')}>{m.spiritual_father_name || <span style={{ color: '#dc2626', fontSize: 12 }}>{t('members.unassigned')}</span>}</td>
                    <td data-label={t('members.dependents')}>
                      {hasDeps
                        ? <span className="pill pill-purple"><FiUsers style={{ fontSize: 10 }} /> {m.dependent_count} {t('members.dependents').toLowerCase()}</span>
                        : <span style={{ color: '#9ca3af', fontSize: 12 }}>{t('common.none')}</span>}
                    </td>
                    <td data-label={t('common.actions')} onClick={e => e.stopPropagation()}>
                      <div className="action-btns" style={{ gap: 10 }}>
                        <button
                          className="btn btn-sm"
                          onClick={() => navigate(`/members/profile/${m.id}`)}
                          style={{ background: '#4f46e5', color: '#fff', border: '1px solid #4338ca', padding: '7px 14px', fontSize: 13, fontWeight: 600 }}
                        >{t('members.profile')}</button>
                        {canManage(m) && (
                          <button
                            className="btn btn-sm"
                            onClick={() => openStatusModal(m)}
                            style={{ background: '#0d9488', color: '#fff', border: '1px solid #0f766e', padding: '7px 14px', fontSize: 13, fontWeight: 600 }}
                          >{t('common.status')}</button>
                        )}
                        {user?.role === 'admin' && <button className="btn-icon danger" onClick={() => handleDelete(m)}><FiTrash2 /></button>}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && hasDeps && (
                    <tr>
                      <td colSpan={10} style={{ padding: 0, background: '#f8f7ff' }}>
                        <div style={{ padding: '12px 48px 16px' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                            {t('allMembers.dependentsUnder', { name: `${m.first_name} ${m.last_name}` })}
                          </div>
                          {!memberDependents[m.id] ? (
                            <p style={{ fontSize: 13, color: '#9ca3af' }}>{t('common.loading')}</p>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                              {memberDependents[m.id].map(d => <DependentCard key={d.id} d={d} />)}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* View Member Modal */}
      {viewMember && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 680 }}>
            <div className="modal-header">
              <div>
                <h3>{viewMember.title ? `${viewMember.title} ` : ''}{viewMember.first_name} {viewMember.middle_name || ''} {viewMember.last_name}</h3>
                <p>
                  <span className={`pill ${statusPill[viewMember.membership_status]}`} style={{ marginRight: 8 }}>{statusLabel[viewMember.membership_status] || viewMember.membership_status}</span>
                  {t('members.spiritualFather')}: <strong>{viewMember.spiritual_father_name || t('members.unassigned')}</strong>
                </p>
              </div>
              <button className="btn-icon" onClick={() => setViewMember(null)}><FiX /></button>
            </div>
            <div className="modal-body">
              {/* Card number */}
              {viewMember.card_number && (
                <div style={{ background: '#eef2ff', border: '1px solid #a5b4fc', borderRadius: 8, padding: '8px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, color: '#4f46e5', fontWeight: 600 }}>{t('allMembers.membershipCardNo')}</span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15, color: '#3730a3' }}>{viewMember.card_number}</span>
                </div>
              )}

              {/* Info grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '6px 20px', marginBottom: 20, fontSize: 13 }}>
                {(viewMember.phone || viewMember.whatsapp) && <div><span style={{ color: '#6b7280' }}>{t('common.phone')}:</span> <PhoneLink phone={viewMember.phone} whatsapp={viewMember.whatsapp} /></div>}
                {viewMember.gender && <div><span style={{ color: '#6b7280' }}>{t('members.gender')}:</span> {viewMember.gender}</div>}
                {viewMember.department && <div><span style={{ color: '#6b7280' }}>{t('allMembers.department')}:</span> {viewMember.department}</div>}
                {viewMember.zone && <div><span style={{ color: '#6b7280' }}>{t('allMembers.zone')}:</span> {viewMember.zone}</div>}
                {viewMember.join_date && <div><span style={{ color: '#6b7280' }}>{t('allMembers.joined')}:</span> {viewMember.join_date}</div>}
              </div>

              {/* Dependents */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <strong style={{ fontSize: 13 }}>{t('allMembers.dependentsHeader', { count: viewMember.dependents?.length || 0 })}</strong>
                  <button className="btn btn-sm btn-secondary" onClick={() => setAddingDep(!addingDep)}><FiPlus /> {t('allMembers.add')}</button>
                </div>
                {addingDep && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                    <div className="form-grid">
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>{t('common.name')} <span className="required">*</span></label>
                        <input value={newDep.name} onChange={setD('name')} placeholder={t('allMembers.fullNamePlaceholder')} />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>{t('members.relationship')}</label>
                        <select value={newDep.relationship} onChange={setD('relationship')}>
                          <option value="wife">{t('members.wife')}</option><option value="husband">{t('members.husband')}</option>
                          <option value="son">{t('members.son')}</option><option value="daughter">{t('members.daughter')}</option><option value="other">{t('members.other')}</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>{t('members.yearOfBirth')} {calcAge(newDep.birth_year) !== null && <span style={{ color: '#4f46e5', fontWeight: 400 }}>→ {t('members.age')}: {calcAge(newDep.birth_year)}</span>}</label>
                        <input type="number" value={newDep.birth_year} onChange={setD('birth_year')} placeholder={currentYear - 10} min="1900" max={currentYear} />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>{t('allMembers.spiritualStatus')}</label>
                        <select value={newDep.spiritual_status} onChange={setD('spiritual_status')}>
                          <option value="unknown">{t('followup.unknown')}</option><option value="saved">{t('allMembers.saved')}</option>
                          <option value="not_saved">{t('allMembers.notSaved')}</option><option value="backslidden">{t('allMembers.backslidden')}</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button className="btn btn-sm btn-primary" onClick={handleAddDep} disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setAddingDep(false)}>{t('common.cancel')}</button>
                    </div>
                  </div>
                )}
                {(viewMember.dependents || []).length === 0 ? (
                  <p style={{ fontSize: 13, color: '#9ca3af' }}>{t('members.noDependents')}</p>
                ) : (
                  <table>
                    <thead><tr><th>{t('common.name')}</th><th>{t('members.relationship')}</th><th>{t('members.yearOfBirth')}</th><th>{t('members.age')}</th><th>{t('allMembers.spiritualStatus')}</th><th></th></tr></thead>
                    <tbody>
                      {viewMember.dependents.map(d => (
                        <tr key={d.id}>
                          <td><strong>{d.name}</strong></td>
                          <td><span className={`pill ${relColors[d.relationship] || 'pill-gray'}`} style={{ textTransform: 'capitalize' }}>{t(`members.${d.relationship}`, d.relationship)}</span></td>
                          <td>{d.birth_year || '—'}</td>
                          <td>{d.birth_year ? `${calcAge(d.birth_year)} ${t('allMembers.yrs')}` : '—'}</td>
                          <td style={{ textTransform: 'capitalize' }}>{d.spiritual_status?.replace('_', ' ') || '—'}</td>
                          <td><button className="btn-icon danger" onClick={() => handleDeleteDep(d.id)}><FiTrash2 /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Follow-ups */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <strong style={{ fontSize: 13 }}>{t('allMembers.followupHistory', { count: viewMember.followups?.length || 0 })}</strong>
                  <button className="btn btn-sm btn-secondary" onClick={() => setAddingFollowup(!addingFollowup)}><FiPlus /> {t('allMembers.record')}</button>
                </div>
                {addingFollowup && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                    <div className="form-grid">
                      <div className="form-group" style={{ marginBottom: 0 }}><label>{t('common.date')}</label><input type="date" value={newFollowup.date} onChange={setF('date')} /></div>
                      <div className="form-group" style={{ marginBottom: 0 }}><label>{t('common.status')}</label>
                        <select value={newFollowup.status} onChange={setF('status')}>
                          <option value="unknown">{t('followup.unknown')}</option><option value="good">{t('followup.good')}</option>
                          <option value="needs_attention">{t('followup.needsAttention')}</option><option value="critical">{t('followup.critical')}</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}><label>{t('followup.nextFollowup')}</label><input type="date" value={newFollowup.next_followup_date} onChange={setF('next_followup_date')} /></div>
                      <div className="form-group form-full" style={{ marginBottom: 0 }}><label>{t('common.notes')}</label><textarea value={newFollowup.notes} onChange={setF('notes')} rows={2} /></div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button className="btn btn-sm btn-primary" onClick={handleAddFollowup} disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setAddingFollowup(false)}>{t('common.cancel')}</button>
                    </div>
                  </div>
                )}
                {(viewMember.followups || []).length === 0 ? (
                  <p style={{ fontSize: 13, color: '#9ca3af' }}>{t('allMembers.noFollowupsRecorded')}</p>
                ) : viewMember.followups.map(f => (
                  <div key={f.id} style={{ padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div>
                        <strong style={{ fontSize: 13 }}>{f.date}</strong>
                        {f.status && <span className={`pill ${f.status === 'good' ? 'pill-green' : f.status === 'critical' ? 'pill-red' : 'pill-yellow'}`} style={{ marginLeft: 8 }}>{t(`followup.${f.status === 'needs_attention' ? 'needsAttention' : f.status}`, f.status.replace('_', ' '))}</span>}
                      </div>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{t('allMembers.byPerson')} {f.done_by_name || t('allMembers.unknownPerson')}</span>
                    </div>
                    {f.notes && <p style={{ fontSize: 13, marginTop: 4 }}>{f.notes}</p>}
                    {f.next_followup_date && <p style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{t('allMembers.nextLabel')}: {f.next_followup_date}</p>}
                  </div>
                ))}
              </div>

              {/* Status History */}
              {viewMember.statusHistory && viewMember.statusHistory.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <strong style={{ fontSize: 13 }}>{t('allMembers.statusHistoryCount', { count: viewMember.statusHistory.length })}</strong>
                  <div style={{ marginTop: 10 }}>
                    {viewMember.statusHistory.map((h, i) => (
                      <div key={h.id} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'flex-start' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#7c3aed', marginTop: 5, flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {h.old_status && <span className={`pill ${statusPill[h.old_status] || 'pill-gray'}`} style={{ fontSize: 11 }}>{statusLabel[h.old_status] || h.old_status}</span>}
                              {h.old_status && <span style={{ color: '#9ca3af', fontSize: 12 }}>→</span>}
                              <span className={`pill ${statusPill[h.new_status] || 'pill-gray'}`} style={{ fontSize: 11 }}>{statusLabel[h.new_status] || h.new_status}</span>
                            </div>
                            <span style={{ fontSize: 11, color: '#9ca3af' }}>{h.changed_at?.slice(0, 10)} · {h.changed_by_name}</span>
                          </div>
                          {(h.reason || h.custom_reason) && <p style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>{t('myProfile.reasonLabel')}: {h.custom_reason || h.reason}</p>}
                          {h.notes && <p style={{ fontSize: 12, color: '#374151', marginTop: 2, fontStyle: 'italic' }}>{h.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {statusModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <div>
                <h3>{t('allMembers.changeMemberStatus')}</h3>
                <p>{statusModal.first_name} {statusModal.middle_name || ''} {statusModal.last_name} · {t('allMembers.current')}: <span className={`pill ${statusPill[statusModal.membership_status] || 'pill-gray'}`} style={{ fontSize: 11 }}>{statusLabel[statusModal.membership_status] || statusModal.membership_status}</span></p>
              </div>
              <button className="btn-icon" onClick={() => setStatusModal(null)}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('allMembers.newStatus')} <span className="required">*</span></label>
                <select value={statusForm.new_status} onChange={e => setStatusForm(p => ({ ...p, new_status: e.target.value, reason: '', custom_reason: '' }))}>
                  <option value="">{t('allMembers.selectNewStatus')}</option>
                  {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>

              {statusForm.new_status && (
                <div className="form-group">
                  <label>{t('myProfile.reasonLabel')}</label>
                  <select value={statusForm.reason} onChange={e => setStatusForm(p => ({ ...p, reason: e.target.value, custom_reason: '' }))}>
                    <option value="">{t('allMembers.selectReason')}</option>
                    {(REASONS[statusForm.new_status] || REASONS.other).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
              )}

              {statusForm.reason === 'Other' || statusForm.new_status === 'other' ? (
                <div className="form-group">
                  <label>{t('allMembers.specifyReason')} <span className="required">*</span></label>
                  <input value={statusForm.custom_reason} onChange={e => setStatusForm(p => ({ ...p, custom_reason: e.target.value }))} placeholder={t('allMembers.writeReason')} />
                </div>
              ) : null}

              <div className="form-group">
                <label>{t('common.notes')}</label>
                <textarea value={statusForm.notes} onChange={e => setStatusForm(p => ({ ...p, notes: e.target.value }))} placeholder={t('allMembers.additionalNotes')} rows={3} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setStatusModal(null)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleStatusChange} disabled={saving}>
                {saving ? t('common.saving') : t('allMembers.confirmStatusChange')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModal && (
        <div className="modal-overlay" onClick={() => !deleteModal.busy && setDeleteModal(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg,#dc2626,#b91c1c)', color: '#fff' }}>
              <h3 style={{ color: '#fff' }}>{t('allMembers.deleteMember')}</h3>
              <button className="btn-icon" onClick={() => !deleteModal.busy && setDeleteModal(null)} style={{ color: '#fff' }}><FiX /></button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: 14, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, color: '#991b1b' }}>
                {t('allMembers.deleteWarning', { name: `${deleteModal.member.first_name} ${deleteModal.member.last_name}` })}
              </div>
              <div className="form-group">
                <label>{t('allMembers.confirmAdminPassword')} <span className="required">*</span></label>
                <input
                  type="password"
                  autoFocus
                  value={deleteModal.password}
                  onChange={e => setDeleteModal(p => ({ ...p, password: e.target.value, error: '' }))}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !deleteModal.busy) confirmDelete();
                    else if (e.key === 'Escape' && !deleteModal.busy) setDeleteModal(null);
                  }}
                  placeholder={t('allMembers.adminPasswordPlaceholder')}
                />
                {deleteModal.error && (
                  <small style={{ color: '#dc2626', fontSize: 12, marginTop: 6, display: 'block' }}>
                    {deleteModal.error}
                  </small>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteModal(null)} disabled={deleteModal.busy}>{t('common.cancel')}</button>
              <button
                className="btn"
                onClick={confirmDelete}
                disabled={deleteModal.busy || !deleteModal.password}
                style={{ background: '#dc2626', color: '#fff', border: '1px solid #b91c1c' }}
              >
                {deleteModal.busy ? t('common.saving') : t('allMembers.confirmDelete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {importReport && (
        <div className="modal-overlay" onClick={() => setImportReport(null)}>
          <div className="modal" style={{ maxWidth: 640, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>Import report</h3>
                <p>{importReport.imported} of {importReport.total} imported · {importReport.errors.length} skipped</p>
              </div>
              <button className="btn-icon" onClick={() => setImportReport(null)}><FiX /></button>
            </div>
            <div style={{ padding: '12px 20px', overflowY: 'auto' }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                Skipped rows (row 2 = first data row after the header):
              </div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                {importReport.errors.map((err, i) => {
                  // err looks like "Row 76: missing first or last name" — try to
                  // pluck the row number so we can show the offending name too.
                  const m = err.match(/^Row (\d+): (.+)$/);
                  const csvRow = m ? parseInt(m[1], 10) : null;
                  const reason = m ? m[2] : err;
                  const dataIdx = csvRow ? csvRow - 2 : -1;
                  const row = dataIdx >= 0 ? importReport.rows[dataIdx] : null;
                  const name = row ? [row.first_name, row.middle_name, row.last_name].filter(Boolean).join(' ').trim() : '';
                  return (
                    <div key={i} style={{ padding: '10px 14px', borderBottom: i < importReport.errors.length - 1 ? '1px solid #f3f4f6' : 'none', fontSize: 13 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {csvRow && <span style={{ background: '#fee2e2', color: '#b91c1c', fontSize: 11, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>Row {csvRow}</span>}
                        {name && <strong>{name}</strong>}
                      </div>
                      <div style={{ color: '#6b7280', marginTop: 4 }}>{reason}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setImportReport(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default AllMembers;
